import React, { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Result, Spin } from 'antd'
import { camelizeKeys } from 'humps'
import { useTranslation } from 'react-i18next'
import { fetchSiteFormPreview } from '../public-form/api'
import { Configuration, configuration } from '../public-form/public-form'
import { PublicFormPage } from '../public-form/components/public-form-page'

export const Route = createFileRoute('/apply-preview/$siteKey')({ component: FormStylePreview })

/**
 * The apply form with sample texts, shown in an iframe in the WordPress plugin while the admin edits the form style.
 * Style changes arrive by postMessage, only from the site's own origin. Nothing can be submitted.
 */
function FormStylePreview() {
    const { siteKey } = Route.useParams()
    const { t } = useTranslation()
    const [styled, setStyled] = useState<Configuration | null>(null)
    const query = useQuery({
        queryKey: ['site-form-preview', siteKey],
        queryFn: () => fetchSiteFormPreview(siteKey),
        retry: false,
    })
    const origin = query.data?.origin
    const sample = query.data?.configuration

    useEffect(() => {
        if (!origin || !sample) {
            return
        }

        const isStyleMessage = (event: MessageEvent) =>
            event.origin === origin && event.source === window.parent && event.data?.type === 'evaa-form-preview'

        const receive = (event: MessageEvent) => {
            if (!isStyleMessage(event)) {
                return
            }

            // The sample texts with the style being edited; an invalid style (for example while typing a colour) is
            // skipped.
            const style = camelizeKeys(event.data.style ?? {}) as object
            const parsed = configuration.safeParse({
                ...sample,
                ...style,
            })

            if (parsed.success) {
                setStyled(parsed.data)
            }
        }

        window.addEventListener('message', receive)
        // Tells the plugin it can send the style now.
        window.parent.postMessage({ type: 'evaa-form-preview-ready' }, origin)

        return () => window.removeEventListener('message', receive)
    }, [origin, sample])

    if (query.isPending) {
        return (
            <div className="p-10 text-center">
                <Spin />
            </div>
        )
    }

    if (query.isError) {
        return (
            <Result
                status="404"
                title={t('Form unavailable')}
            />
        )
    }

    return (
        <PublicFormPage
            configuration={styled ?? query.data.configuration}
            preview
        />
    )
}
